<template>
    <cic-popup
        :saveable="!useDefinitions"
        class="no-padding"
        title="Edit User Licences"
        @blur="$emit('blur')"
        @input="handleApply"
    >
        <ul class="item-form" style="padding: 10px 10px 10px 26px">
            <li class="form-label-first">{{ $t('Only Show Assigned Licences') }}</li>
            <li><cic-checkbox id="only_assigned" :value="onlyAssigned" @input="(val) => (onlyAssigned = val)" /></li>
            <template v-if="!useDefinitions">
                <li class="form-label">{{ $t('Assign All Licences') }}</li>
                <li><cic-checkbox id="assign_all" :value="assignAll" @input="handleAssignAll" /></li>
                <li class="form-label">{{ $t('Remove All Licences') }}</li>
                <li><cic-checkbox id="remove_all" :value="removeAll" @input="handleRemoveAll" /></li>
                <li class="form-label">{{ $t('Licence Template') }}</li>
                <li>
                    <cic-select
                        id="licence_template"
                        :value="templateId"
                        :options="validTemplates"
                        @input="selectTemplate"
                    />
                </li>
                <li class="form-label">{{ $t('Licence Expiry') }}</li>
                <li>
                    <cic-select
                        id="licence_expiry"
                        :value="expiry"
                        :options="[
                            { value: 1, label: $t('Closest to expiry') },
                            { value: -1, label: $t('Furthest from expiry') },
                        ]"
                        @input="(val) => (expiry = val)"
                    />
                </li>
            </template>
        </ul>
        <table id="tblLicences" class="cic-table">
            <thead>
                <tr>
                    <th>{{ $t('Licence') }}</th>
                    <th>{{ $t('Dependent Licence') }}</th>
                    <th>{{ $t('Date Assigned') }}</th>
                    <th>{{ $t('Expires') }}</th>
                    <th style="width: 90px">{{ $t('Assigned') }}</th>
                </tr>
            </thead>
            <tbody>
                <template v-for="group in licenceGroups">
                    <tr>
                        <td class="primary" colspan="5">{{ group }}</td>
                    </tr>
                    <tr v-for="quantity in filtered.filter((x) => x.group === group)">
                        <td>{{ quantity.name }}</td>
                        <td>
                            <span v-if="quantity.required.length === 0">-</span>
                            <ul v-else>
                                <li
                                    v-for="required in quantities.filter((x) =>
                                        quantity.required.includes(x.quantityId),
                                    )"
                                >
                                    {{ required.name }}
                                </li>
                            </ul>
                        </td>
                        <template v-if="userLicences.some((x) => x.quantityId === quantity.quantityId)">
                            <td>
                                {{
                                    formatStartDate(
                                        userLicences.find((x) => x.quantityId === quantity.quantityId).assigned,
                                    )
                                }}
                            </td>
                            <td>
                                {{ formatEndDate(userLicences.find((x) => x.quantityId === quantity.quantityId).end) }}
                            </td>
                        </template>
                        <template v-else-if="available.includes(quantity.quantityId)">
                            <td>-</td>
                            <td>
                                {{ formatEndDate(assignable.find((x) => x.quantityId === quantity.quantityId).end) }}
                            </td>
                        </template>
                        <template v-else>
                            <td>-</td>
                            <td>-</td>
                        </template>
                        <td>
                            <cic-checkbox
                                v-if="available.includes(quantity.quantityId) || useDefinitions"
                                :enabled="!blocked.includes(quantity.quantityId) && !useDefinitions"
                                :value="selected.includes(quantity.quantityId)"
                                @input="(val) => handleClick(quantity.quantityId, val)"
                            />
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
    </cic-popup>
</template>

<script>
    import CicPopup from '@/components/CicPopup';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicSelect from '@/components/inputs/CicSelect';
    import {
        deselect,
        formatEndDate,
        formatStartDate,
        getBlocked,
        getGroups,
        getUserLicences,
        select,
    } from '../LicenceUtils';

    export default {
        name: 'EditUserLicences',
        components: {
            CicCheckbox,
            CicPopup,
            CicSelect,
        },
        props: {
            gracePeriod: {
                type: Boolean,
                default: false,
            },
            licences: {
                type: Array,
                required: true,
            },
            quantities: {
                type: Array,
                required: true,
            },
            templates: {
                type: Array,
                required: true,
            },
            useDefinitions: {
                type: Boolean,
                required: true,
            },
            user: {
                type: Object,
                required: true,
            },
        },
        emits: ['blur', 'input'],
        data() {
            return {
                assignAll: false,
                available: [],
                blocked: [],
                expiry: 1,
                onlyAssigned: false,
                removeAll: false,
                selected: [],
                templateId: null,
                userLicences: [],
                validTemplates: [],
            };
        },
        computed: {
            assignable() {
                return this.licences
                    .filter((x) => x.assigned.length < x.quantity)
                    .sort((a, b) => (a.end > b.end ? this.expiry : -this.expiry));
            },
            filtered() {
                if (this.onlyAssigned) {
                    return this.quantities.filter((x) => this.selected.includes(x.quantityId));
                }

                return this.quantities;
            },
            licenceGroups() {
                return getGroups(this.quantities, this.filtered);
            },
        },
        created() {
            this.userLicences = getUserLicences(this.user, this.licences);
            this.selected = this.userLicences.map((x) => x.quantityId);
            const available = [...this.selected].concat(
                this.quantities
                    .filter((x) => !this.selected.includes(x.quantityId) && x.total > x.assigned)
                    .map((x) => x.quantityId),
            );
            this.available = available.filter(
                (x) => !this.quantities.find((y) => y.quantityId === x).allRequired.some((z) => !available.includes(z)),
            );
            this.validTemplates = this.templates.filter((x) => !x.quantities.some((y) => !this.available.includes(y)));

            this.blocked = getBlocked(this.quantities, this.gracePeriod, this.userLicences);
        },
        methods: {
            formatEndDate,
            formatStartDate,
            handleApply() {
                const current = this.userLicences.map((x) => x.quantityId);
                const assignments = this.selected
                    .filter((x) => !current.includes(x))
                    .map((x) => this.assignable.find((y) => y.quantityId === x).id);
                const removals = this.userLicences
                    .filter((x) => !this.selected.includes(x.quantityId))
                    .map((x) => x.guid);
                this.$emit('input', assignments, removals);
            },

            handleAssignAll(val) {
                const current = this.userLicences.map((x) => x.quantityId);
                this.assignAll = val;
                if (val) {
                    this.selected = current.concat(this.available.filter((x) => !current.includes(x)));
                } else {
                    this.selected = current;
                }
            },

            handleClick(quantityId, val) {
                if (val) {
                    this.selected = select(
                        this.quantities,
                        this.selected,
                        quantityId,
                        this.$t('TEMPLATE_SELECT_PARENTS'),
                        true,
                    );
                } else {
                    this.selected = deselect(
                        this.quantities,
                        this.selected,
                        quantityId,
                        this.$t('TEMPLATE_DESELECT_CHILDREN'),
                        true,
                    );
                }
            },

            handleRemoveAll(val) {
                if (val) {
                    if (!this.blocked.length || confirm(this.$t('WITHIN_MINIMUM_PERIOD'))) {
                        this.assignAll = false;
                        this.removeAll = val;
                        this.selected = [...this.blocked];
                    }
                } else {
                    this.assignAll = false;
                    this.removeAll = val;
                    this.selected = this.userLicences.map((x) => x.quantityId);
                }
            },

            selectTemplate(templateId) {
                this.templateId = templateId;
                const current = this.userLicences.map((x) => x.quantityId);
                const licenceTemplate = this.templates.find((x) => x.templateId === templateId);
                this.selected = current.concat(licenceTemplate.quantities.filter((x) => !current.includes(x)));
            },
        },
    };
</script>
